


export const APPLICATION_STATUSES = [
  "applied",
  "submitted",
  "under_review",
  "screening",
  "shortlisted",
  "interview_scheduled",
  "interviewed",
  "interview_completed",
  "passed",
  "failed",
  "assessment_pending",
  "assessment_completed",
  "offer_extended",
  "offer_sent",
  "offer_accepted",
  "offer_rejected",
  "hired",
  "joined",
  "rejected",
  "not_selected",
  "withdrawn",
  "not_joined",
  "on_hold",
] as const;

export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const STATUS_OPTIONS = APPLICATION_STATUSES.map((s) => ({
  value: s,
  label: s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
}));

export const STATUS_COLORS: Record<string, string> = {
  applied: "#FFA500",
  submitted: "#FFA500",
  under_review: "#3498DB",
  screening: "#3498DB",
  shortlisted: "#2ECC71",
  interview_scheduled: "#9B59B6",
  interviewed: "#9B59B6",
  interview_completed: "#9B59B6",
  passed: "#00C851",
  failed: "#E74C3C",
  assessment_pending: "#F39C12",
  assessment_completed: "#2ECC71",
  offer_extended: "#27AE60",
  offer_sent: "#27AE60",
  offer_accepted: "#16A085",
  offer_rejected: "#E74C3C",
  hired: "#00C851",
  joined: "#00C851",
  rejected: "#E74C3C",
  not_selected: "#E74C3C",
  withdrawn: "#95A5A6",
  not_joined: "#E67E22",
  on_hold: "#F39C12",
};


export const APPLICATION_STAGES = APPLICATION_STATUSES;
export type ApplicationStage = ApplicationStatus;
export const STAGE_OPTIONS = STATUS_OPTIONS;
export const STAGE_COLORS: Record<string, string> = STATUS_COLORS;

// ─────────────────── LEAN 7-STATUS ENUM ─────────────────────
export const LEAN_APPLICATION_STATUSES = [
  "received",
  "screening",
  "in_pipeline",
  "offered",
  "hired",
  "rejected",
  "withdrawn",
] as const;

export type LeanApplicationStatus = (typeof LEAN_APPLICATION_STATUSES)[number];

export const LEAN_STATUS_OPTIONS = LEAN_APPLICATION_STATUSES.map((s) => ({
  value: s,
  label: s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
}));

export const LEAN_STATUS_COLORS: Record<LeanApplicationStatus, string> = {
  received: "#FFA500",
  screening: "#3498DB",
  in_pipeline: "#9B59B6",
  offered: "#27AE60",
  hired: "#00C851",
  rejected: "#E74C3C",
  withdrawn: "#95A5A6",
};

// Round-level status colors
export const ROUND_STATUS_COLORS: Record<string, string> = {
  pending: "#94A3B8",
  scheduled: "#3498DB",
  in_progress: "#F39C12",
  passed: "#00C851",
  failed: "#E74C3C",
  skipped: "#95A5A6",
  cancelled: "#64748B",
  no_show: "#E67E22",
};


export const BULK_STATUS_VALUES = [
  "screening",
  "shortlisted",
  "interview_scheduled",
  "rejected",
  "on_hold",
] as const;


export const JOB_STATUSES = ["active", "closed", "draft"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  active: "success",
  closed: "danger",
  draft: "warning",
};

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: "full-time", label: "Full Time" },
  { value: "part-time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
];


export const WORK_MODES = ["onsite", "remote", "hybrid"] as const;

export const WORK_MODE_OPTIONS = WORK_MODES.map((m) => ({
  value: m,
  label: m.charAt(0).toUpperCase() + m.slice(1),
}));


export const EXPERIENCE_LEVELS = [
  { value: "0-1", label: "Fresher (0-1 year)" },
  { value: "1-3", label: "Junior (1-3 years)" },
  { value: "3-5", label: "Mid (3-5 years)" },
  { value: "5-8", label: "Senior (5-8 years)" },
  { value: "8+", label: "Lead (8+ years)" },
];


export const DEPARTMENT_OPTIONS = [
  { value: "Engineering", label: "Engineering" },
  { value: "Product", label: "Product" },
  { value: "Design", label: "Design" },
  { value: "Marketing", label: "Marketing" },
  { value: "Sales", label: "Sales" },
  { value: "HR", label: "Human Resources" },
  { value: "Finance", label: "Finance" },
  { value: "Operations", label: "Operations" },
];


export const SOURCE_OPTIONS = [
  { value: "career_page", label: "Career Page" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "indeed", label: "Indeed" },
  { value: "naukri", label: "Naukri" },
  { value: "referral", label: "Referral" },
  { value: "direct", label: "Direct Email" },
];


export const SCORE_RANGE_OPTIONS = [
  { value: "high", label: "> 80% (Strong Match)" },
  { value: "mid", label: "60–80% (Moderate)" },
  { value: "low", label: "< 60% (Low Match)" },
];


export const ROUND_TYPES = [
  { value: "screening_call", label: "Screening Call" },
  { value: "phone_interview", label: "Phone Interview" },
  { value: "technical_round", label: "Technical Round" },
  { value: "coding_round", label: "Coding Round" },
  { value: "system_design", label: "System Design" },
  { value: "take_home_task", label: "Take-Home Task" },
  { value: "case_study", label: "Case Study" },
  { value: "panel_interview", label: "Panel Interview" },
  { value: "managerial_round", label: "Managerial Round" },
  { value: "hr_round", label: "HR Round" },
  { value: "culture_fit", label: "Culture Fit" },
  { value: "background_check", label: "Background Check" },
  { value: "reference_check", label: "Reference Check" },
  { value: "final_round", label: "Final Round" },
];

export const ROUND_DURATIONS = [
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
];


export const INTERVIEW_MODES = [
  { value: "video", label: "Video Call" },
  { value: "in_person", label: "In Person" },
  { value: "phone", label: "Phone" },
];


export const MEETING_PLATFORMS = [
  { value: "zoom", label: "Zoom" },
  { value: "google_meet", label: "Google Meet" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "phone", label: "Phone" },
  { value: "office", label: "Office" },
];

export const EVALUATION_CRITERIA = [
  "Technical Skills",
  "Communication",
  "Culture Fit",
  "Problem Solving",
  "Leadership",
];


export const RECOMMENDATION_OPTIONS = [
  { value: "strong_hire", label: "Strong Hire", color: "success" },
  { value: "hire", label: "Hire", color: "success" },
  { value: "maybe", label: "Maybe", color: "warning" },
  { value: "no_hire", label: "No Hire", color: "danger" },
  { value: "strong_no_hire", label: "Strong No Hire", color: "danger" },
];


export const CANCELLATION_REASONS = [
  { value: "candidate_unavailable", label: "Candidate Unavailable" },
  { value: "interviewer_unavailable", label: "Interviewer Unavailable" },
  { value: "position_filled", label: "Position Filled" },
  { value: "candidate_withdrawn", label: "Candidate Withdrawn" },
  { value: "other", label: "Other" },
];


export const REJECTION_REASONS = [
  { value: "not_qualified", label: "Not Qualified" },
  { value: "position_filled", label: "Position Filled" },
  { value: "overqualified", label: "Overqualified" },
  { value: "salary_mismatch", label: "Salary Mismatch" },
  { value: "location_issue", label: "Location Issue" },
  { value: "other", label: "Other" },
];


export const DOCUMENT_TYPES = [
  "Photo",
  "ID Proof",
  "Address Proof",
  "Educational Certificates",
  "Experience Letters",
  "Salary Slips",
  "Relieving Letter",
  "Background Verification Consent",
  "Portfolio",
  "Code Samples",
];

export const DOCUMENT_STATUSES = [
  "Pending",
  "Uploaded",
  "Under Review",
  "Approved",
  "Rejected",
] as const;


export const REFERRAL_RELATIONSHIPS = [
  { value: "friend", label: "Friend" },
  { value: "family", label: "Family" },
  { value: "colleague", label: "Colleague" },
  { value: "ex_colleague", label: "Ex-Colleague" },
  { value: "other", label: "Other" },
];


export const REFERRAL_STATUSES = [
  "pending",
  "under_review",
  "shortlisted",
  "rejected",
  "interviewed",
  "selected",
  "joined",
  "withdrawn",
] as const;


export const PLATFORMS = [
  { key: "career_page", label: "Career Page", icon: "ti-world" },
  { key: "linkedin", label: "LinkedIn", icon: "ti-brand-linkedin" },
  { key: "indeed", label: "Indeed", icon: "ti-briefcase" },
  { key: "naukri", label: "Naukri", icon: "ti-building" },
];


export const EMAIL_TEMPLATE_TYPES = [
  "application_received",
  "interview_scheduled",
  "interview_rescheduled",
  "offer_extended",
  "application_rejected",
  "documents_requested",
  "onboarding_welcome",
  "assessment_invitation",
  "referral_thank_you",
  "application_waitlisted",
];


export const EMAIL_TYPE_OPTIONS = [
  { value: "general", label: "General" },
  { value: "interview_invite", label: "Interview Invite" },
  { value: "offer", label: "Offer" },
  { value: "rejection", label: "Rejection" },
  { value: "document_request", label: "Document Request" },
];


export const INTEGRATION_KEYS = [
  { key: "linkedin", label: "LinkedIn Talent Solutions" },
  { key: "indeed", label: "Indeed Publisher" },
  { key: "naukri", label: "Naukri Recruiter" },
  { key: "resume_parser", label: "Resume Parser (RChilli)" },
  { key: "openai", label: "OpenAI (GPT-4o)" },
  { key: "gemini", label: "Google Gemini" },
  { key: "anthropic", label: "Anthropic Claude" },
];


export const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "#E74C3C" },
  { value: "high", label: "High", color: "#FFA500" },
  { value: "normal", label: "Normal", color: "#3498DB" },
  { value: "low", label: "Low", color: "#95A5A6" },
];


export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
